import { FC, useState } from 'react';
import { Button, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { RootReducer, StreamingReducer } from '../../../../models/Redux';
import { useStyles } from './styles';

import sendIcon from '../../../../assets/streaming/actions/send.svg';

import { thunkSendMessage } from '../../../../store/actions/Streaming.actions';

const maxMessageLength = 200;

interface ChatInputProps {
    mobileLayout?: boolean;
    enabled?: boolean;
}

const ChatInput: FC<ChatInputProps> = ({ mobileLayout, enabled }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [message, setMessage] = useState<string>('');

    const {
        streamingState: { current_state: currentState },
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    const renderChatInput = () => {
        return currentState?.chat_enabled;
    };

    const sendMessage = () => {
        if (!isEmpty(message) && renderChatInput()) {
            dispatch(
                thunkSendMessage(message, () => {
                    setMessage('');
                }),
            );
        }
    };

    const sendMessageDesktop = () => {
        if (!isEmpty(message) && renderChatInput() && message.length <= maxMessageLength) {
            dispatch(
                thunkSendMessage(message, () => {
                    setMessage('');
                }),
            );
        }
    };

    const keyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === 13 || e.code === 'Enter' || e.code === 'NumpadEnter') {
            if (mobileLayout) {
                sendMessage();
            } else {
                sendMessageDesktop();
            }
        }
    };

    if (!renderChatInput() && mobileLayout) {
        return null;
    }

    if (mobileLayout) {
        return (
            <Grid
                container
                xs={12}
                justify="center"
                alignContent="center"
                alignItems="center"
                direction="row"
            >
                <Grid container item xs={12} direction="row" className={clsx(classes.container)}>
                    <Grid
                        xs={12}
                        container
                        justify="space-between"
                        alignContent="center"
                        alignItems="center"
                        direction="row"
                    >
                        <Grid item xs={10}>
                            <TextField
                                placeholder={t('Type something...')}
                                value={message}
                                onKeyDown={keyPress}
                                onChange={event => {
                                    const {
                                        target: { value },
                                    } = event;

                                    setMessage(value);
                                }}
                                InputProps={{ disableUnderline: true, className: classes.input }}
                                fullWidth
                            />
                        </Grid>
                        <IconButton onClick={sendMessage} className={clsx(classes.sendMessageIconButton)}>
                            <img className={classes.sendMessageIcon} src={sendIcon} alt={t('Send message')} />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid
            container
            xs={12}
            justify="center"
            alignContent="center"
            alignItems="center"
            direction="column"
            className={classes.desktopChatContainer}
        >
            <Grid container item xs={12} direction="row" className={clsx(classes.desktopContainer)}>
                <Grid
                    xs={12}
                    container
                    justify="space-between"
                    alignContent="center"
                    alignItems="center"
                    direction="row"
                >
                    <Grid item xs={11}>
                        <TextField
                            placeholder={t('Send a message')}
                            value={message}
                            multiline
                            disabled={!enabled || !renderChatInput()}
                            onKeyDown={keyPress}
                            onChange={event => {
                                const {
                                    target: { value },
                                } = event;

                                setMessage(value);
                            }}
                            InputProps={{ disableUnderline: true, className: classes.desktopInput }}
                            fullWidth
                            className={classes.textArea}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                container
                item
                xs={12}
                direction="row"
                justify="space-between"
                alignContent="center"
                alignItems="center"
                className={classes.sendButtonContainer}
            >
                <Typography
                    className={clsx(
                        classes.maxMessageLength,
                        message.length > maxMessageLength ? classes.errorColor : '',
                    )}
                >
                    {message.length}/{maxMessageLength}
                </Typography>
                <Button variant="contained" className={classes.sendButton} onClick={sendMessageDesktop}>
                    <Typography>{t('Send')}</Typography>
                </Button>
            </Grid>
        </Grid>
    );
};

export default ChatInput;
